import {useMutation, useQueryClient} from '@github-ui/react-query'
import type {
  CreateTaskParams,
  CreateJobParams,
  UnhydratedTask,
  OptimisticTask,
  CreateTaskMutationParams,
} from '../types/task'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {fetchCAPI} from '../utils/fetch-capi'
import {uuid} from '../utils/uuid'
import {agentTasksQueryKey} from './useTasks'

// temporary, for createTaskViaMonolith function parameters
type CreateTaskViaMonolithParams = CreateTaskMutationParams & {
  isBranchSelectionEnabled: boolean
}

export function useCreateTask({
  apiURL,
  onCreateError,
}: {
  apiURL: string
  onCreateError?: {
    onError?: (error: Error, variables: CreateTaskMutationParams) => void
  }
}) {
  const queryClient = useQueryClient()
  const isBranchSelectionEnabled = isFeatureEnabled('copilot_immersive_agent_branch_selection')
  const isCreateSessionViaFrontendEnabled = isFeatureEnabled('cca_create_session_from_front_end')

  const createTaskMutation = useMutation<
    string,
    Error,
    CreateTaskMutationParams,
    {previousTasks: Array<UnhydratedTask | OptimisticTask>}
  >({
    mutationKey: ['createTask'],
    onMutate: async (params: CreateTaskMutationParams) => {
      // Cancel any existing refetches (prevents race conditions)
      await queryClient.cancelQueries({queryKey: agentTasksQueryKey})

      // Snapshot the previous value for potential rollback
      const previousTasks = queryClient.getQueryData<Array<UnhydratedTask | OptimisticTask>>(agentTasksQueryKey) ?? []

      // Add optimistic task to cache
      const optimisticTask = createOptimisticTask(params)
      queryClient.setQueryData(agentTasksQueryKey, [optimisticTask, ...previousTasks])

      // Return context for error handling
      return {previousTasks}
    },
    mutationFn: async (params: CreateTaskMutationParams) => {
      let response
      if (isCreateSessionViaFrontendEnabled) {
        response = await createJob({
          ...params,
          apiURL,
          isBranchSelectionEnabled,
          title: params.problemStatement,
        })
      } else {
        // Fall back to the monolith approach
        response = await createTaskViaMonolith({
          ...params,
          isBranchSelectionEnabled,
        })
      }
      if (!response.ok) {
        throw new Error(`Failed to create task: ${params.clientGeneratedId}`)
      }

      return params.clientGeneratedId
    },
    onError: (error, variables, context) => {
      // Restore previous state (more reliable than filtering)
      if (context?.previousTasks) {
        queryClient.setQueryData(agentTasksQueryKey, context.previousTasks)
      }

      // Call the passed callback with returned error and original variables
      onCreateError?.onError?.(error, variables)
    },
    onSuccess: () => {
      // Invalidate and refetch tasks query to warm up the cache
      queryClient.invalidateQueries({queryKey: agentTasksQueryKey})
    },
  })

  const createTask = (params: CreateTaskParams) => {
    const clientGeneratedId = uuid()
    createTaskMutation.mutate({...params, clientGeneratedId})
  }

  return {
    createTask,
    isCreating: createTaskMutation.isPending,
    createError: createTaskMutation.error,
  }
}

// Helper function to create job via the Jobs API. Should be removed with feature flag cca_create_session_from_front_end
async function createJob({
  clientGeneratedId,
  nwo,
  problemStatement,
  baseRef,
  apiURL,
  isBranchSelectionEnabled,
  eventType,
  title,
}: CreateJobParams): Promise<Response> {
  const endpoint = `/agents/swe/jobs/${nwo}`
  return await fetchCAPI({
    apiURL,
    path: endpoint,
    method: 'POST',
    body: {
      problem_statement: problemStatement,
      pull_request: {
        ...(isBranchSelectionEnabled && {base_ref: `refs/heads/${baseRef}`}),
        ...(title && {title}),
      },
      event_identifiers: [clientGeneratedId],
      event_type: eventType,
    },
  })
}

// Helper function to create tasks via the monolith (fallback approach)
async function createTaskViaMonolith({
  clientGeneratedId,
  nwo,
  problemStatement,
  baseRef,
  isBranchSelectionEnabled,
  eventType,
}: CreateTaskViaMonolithParams): Promise<Response> {
  const endpoint = `/${nwo}/agent-sessions`
  return await verifiedFetchJSON(endpoint, {
    method: 'POST',
    body: {
      problem_statement: problemStatement,
      ...(isBranchSelectionEnabled && {base_ref: baseRef}),
      creation_id: clientGeneratedId,
      event_type: eventType,
    },
  })
}

function createOptimisticTask({
  clientGeneratedId,
  nwo,
  problemStatement,
  baseRef,
  eventType,
}: CreateTaskMutationParams): OptimisticTask {
  return {
    resource_type: 'pull',
    resource_id: -1, // Placeholder until we get real ID from server
    session_count: 1,
    latest_session_updated_at: new Date().toISOString(),
    latest_session_state: 'in_progress',
    event_identifiers: [clientGeneratedId],
    event_type: eventType,
    isOptimistic: true,
    clientGeneratedId,
    nwo,
    problemStatement,
    baseRef,
  }
}
